import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Form, Modal} from 'react-bootstrap';
import {Formik} from 'formik';
import {sharingType} from '../constants';
import Configuration from '../config';
import iconPrivate from '../images/user_icon_private_red.svg';
import iconReplayer from '../images/user_icon_replayer_red.svg';
import iconPublic from '../images/user_icon_public_red.svg';
import {useTranslation} from 'react-i18next';
import * as api from '../api';
import {FaCopy, FaSave, FaTimes} from 'react-icons/fa';

const analysisSharingUrl = (sharingId, target) => {
  const baseUrl = (Configuration.baseUrl || window.location.origin).replace(/\/$/, '');
  if (!sharingId) {
    return '';
  }
  const queryTarget = target ? Object.keys(target).reduce((params, key) => {
    if (!['label', 'description'].includes(key) && target[key] !== null && target[key] !== undefined && target[key] !== '') {
      params[key] = target[key];
    }
    return params;
  }, {}) : null;
  const query = queryTarget && Object.keys(queryTarget).length > 0 ? `?${new URLSearchParams(queryTarget).toString()}` : '';
  return `${baseUrl}/analysis/${sharingId}${query}`;
};

const copyToClipboard = elementId => {
  const inputText = document.getElementById(elementId);
  if (!inputText) {
    return;
  }
  inputText.select();
  inputText.setSelectionRange(0, 99999);
  document.execCommand('copy');
};

const defaultSharingValue = value => (
  value === sharingType.replayer || value === sharingType.public ? value : sharingType.public
);

const PlayerVideoAnalysisSharing = ({request, show, onHide, token, onShared, target}) => {
  const {t} = useTranslation();
  const [currentSharingId, setCurrentSharingId] = useState(request && (request.sharingId || request.sharing_id));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const hasSharingLink = !!currentSharingId;
  const sharingUrl = analysisSharingUrl(currentSharingId, target);

  useEffect(() => {
    setCurrentSharingId(request && (request.sharingId || request.sharing_id));
    setError(null);
  }, [request]);

  if (!request) {
    return null;
  }

  return (
    <Modal className="modal_rp" contentClassName="bg_blue_rp" show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Condividi analisi</Modal.Title>
      </Modal.Header>
      <Formik
        enableReinitialize
        initialValues={{sharing: defaultSharingValue(request.sharing)}}
        onSubmit={async values => {
          setSaving(true);
          setError(null);
          const result = await api.shareVideoAnalysisRequest(request.id, values.sharing, token);
          setSaving(false);
          if (result.status !== 200 && result.status !== 201) {
            setError(t('SAVE_ERROR_WITH_REASON', {reason: result.json && result.json.error ? result.json.error : result.status}));
            return;
          }
          const sharingId = result.json.sharingId || result.json.sharing_id || result.json.id;
          setCurrentSharingId(sharingId);
          if (onShared) {
            onShared({
              ...request,
              sharing: values.sharing,
              sharingId
            });
          }
        }}
      >
        {({handleSubmit, values, setFieldValue}) => (
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <h5>{request.title || 'Analisi partita'}</h5>
              {target && target.label ? <p className="lightblue_text mb-2">Condivisione: {target.label}</p> : null}
              {target && target.description ? <p className="lightblue_text mb-2">{target.description}</p> : null}
              <h6 className="lightblue_text">{t('WHO_CAN_SEE_VIDEO')}</h6>
              <div className="input-group share_radio_rp utente_privato_radio_rp mb-1">
                <label className="form-control" htmlFor="analysis-sharing-private">
                  <img src={iconPrivate} alt={t('ICON_USER_PRIVATE')}/> {t('JUST_ME_PRIVATE')}
                </label>
                <div className="input-group-append">
                  <div className="input-group-text form-check">
                    <input id="analysis-sharing-private" type="radio" className="form-check-input" name="analysis-sharing" checked={values.sharing === sharingType.none} onChange={() => setFieldValue('sharing', sharingType.none)}/>
                  </div>
                </div>
              </div>
              <div className="input-group share_radio_rp utente_replayer_radio_rp mb-1">
                <label className="form-control" htmlFor="analysis-sharing-replayer">
                  <img src={iconReplayer} alt={t('ICON_USER_REPLAYER')}/> {t('COMMUNITY_MEMBERS')}
                </label>
                <div className="input-group-append">
                  <div className="input-group-text form-check">
                    <input id="analysis-sharing-replayer" type="radio" className="form-check-input" name="analysis-sharing" checked={values.sharing === sharingType.replayer} onChange={() => setFieldValue('sharing', sharingType.replayer)}/>
                  </div>
                </div>
              </div>
              <div className="input-group share_radio_rp utente_pubblico_radio_rp mb-4">
                <label className="form-control" htmlFor="analysis-sharing-public">
                  <img src={iconPublic} alt={t('ICON_USER_PUBLIC')}/> {t('ANYONE_PUBLIC')}
                </label>
                <div className="input-group-append">
                  <div className="input-group-text form-check">
                    <input id="analysis-sharing-public" type="radio" className="form-check-input" name="analysis-sharing" checked={values.sharing === sharingType.public} onChange={() => setFieldValue('sharing', sharingType.public)}/>
                  </div>
                </div>
              </div>
              {!hasSharingLink ? (
                <p className="lightblue_text">
                  {t('ANALYSIS_SHARING_SAVE_TO_GENERATE_LINK')}
                </p>
              ) : null}
              {error ? <p className="text-danger">{error}</p> : null}
              <div className="input-group">
                <input readOnly type="text" id="url_video_analysis" className="form-control" value={sharingUrl} placeholder={t('LINK_AVAILABLE_AFTER_SAVE')}/>
                <div className="input-group-append">
                  <button className="btn myvideo-action myvideo-action--share" type="button" disabled={!hasSharingLink} onClick={() => copyToClipboard('url_video_analysis')}><FaCopy aria-hidden="true"/>{t('COPY')}</button>
                </div>
              </div>
            </Modal.Body>
            <div className="modal-footer">
              <button type="submit" className="btn myvideo-action myvideo-action--primary" disabled={saving}><FaSave aria-hidden="true"/>{saving ? t('SAVING') : t('SAVE')}</button>
              <button type="button" className="btn myvideo-action myvideo-action--utility" onClick={onHide}><FaTimes aria-hidden="true"/>{t('CANCEL')}</button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

PlayerVideoAnalysisSharing.propTypes = {
  request: PropTypes.object,
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  token: PropTypes.string,
  onShared: PropTypes.func,
  target: PropTypes.object
};

PlayerVideoAnalysisSharing.defaultProps = {
  request: null,
  show: false,
  token: null,
  onShared: null,
  target: null
};

export default PlayerVideoAnalysisSharing;
